import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import Table from 'LuxKit/Tables/Table'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { drawerBodyInlinePaddingStyles } from './drawerBodyLayout.styles'

const MOBILE_OFFER_PAGE_GALLERY_ASPECT_RATIO = '16 / 9'

export const PRIMARY_GALLERY_IMAGE_CLASS = 'drawer-gallery-primary-image'
export const SECONDARY_GALLERY_IMAGE_CLASS = 'drawer-gallery-secondary-image'

export type GalleryImageCount = 1 | 2 | 3

export const DRAWER_DETAIL_MOBILE_FULL_WIDTH_CLASS = 'mobile-full-width'

interface GridContainerProps {
  $singleImage?: boolean
  $imageCount?: GalleryImageCount
  $matchOfferPageAspect?: boolean
}

function getGridTemplateColumns({ $singleImage, $imageCount }: GridContainerProps) {
  if ($imageCount === 2) return '1fr 1fr'
  return $imageCount === 1 || $singleImage ? '1fr' : '2fr 1fr'
}

function getGridTemplateRows({ $singleImage, $imageCount }: GridContainerProps) {
  if ($imageCount === 2) return '1fr'
  return $imageCount === 1 || $singleImage ? '1fr' : '50% 50%'
}

function getPrimaryImageGridColumn({ $singleImage, $imageCount }: GridContainerProps) {
  if ($imageCount === 2) return 'auto'
  return $imageCount === 1 || $singleImage ? '1 / -1' : '1 / 2'
}

function getPrimaryImageGridRow({ $singleImage, $imageCount }: GridContainerProps) {
  if ($imageCount === 2) return 'auto'
  return $imageCount === 1 || $singleImage ? '1 / -1' : '1 / 3'
}

export const ContentWrapper = styled(Group)`
  margin-block-start: ${rem(24)};
  margin-block-end: ${rem(24)};
  margin-inline-start: 0;
  margin-inline-end: 0;
  padding-block-start: 0;
  padding-block-end: 0;
  ${drawerBodyInlinePaddingStyles}
  width: 100%;
  max-width: ${rem(700)};
  min-width: 0;
  box-sizing: border-box;
`

export const DrawerMobileFullBleedGallery = styled.div`
  inline-size: 100%;
`

export const MapWrapper = styled.div`
  width: 100%;
  aspect-ratio: 16 / 10;
  min-height: ${rem(260)};
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;
`

export const RoundedTable = styled(Table)`
  border-radius: ${props => props.theme.borderRadius.L};
`

export const TipCard = styled.div`
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${props => props.theme.borderRadius.M};
`

export const ThingsToDoCard = styled(ClickableListItem)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
`

export const GridContainer = styled.div<GridContainerProps>`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  gap: ${rem(4)};
  width: 100%;
  height: ${rem(220)};
  position: relative;
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;

  .${PRIMARY_GALLERY_IMAGE_CLASS} {
    grid-column: 1 / -1;
    grid-row: 1 / -1;
  }

  .${SECONDARY_GALLERY_IMAGE_CLASS} {
    display: none;
  }

  &.${DRAWER_DETAIL_MOBILE_FULL_WIDTH_CLASS} {
    ${mediaQueryDown.mobile} {
      height: auto;
      aspect-ratio: ${MOBILE_OFFER_PAGE_GALLERY_ASPECT_RATIO};
      border-radius: 0;
      gap: 0;
    }
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${getGridTemplateColumns};
    grid-template-rows: ${getGridTemplateRows};
    height: ${props => props.$matchOfferPageAspect ? 'auto' : rem(340)};
    aspect-ratio: ${props => props.$matchOfferPageAspect ? 2.78 : 'auto'};

    .${PRIMARY_GALLERY_IMAGE_CLASS} {
      grid-column: ${getPrimaryImageGridColumn};
      grid-row: ${getPrimaryImageGridRow};
    }

    .${SECONDARY_GALLERY_IMAGE_CLASS} {
      display: block;
    }
  }
`

export const GalleryButtons = styled(Group)`
  pointer-events: auto;
  position: absolute;
  bottom: ${rem(16)};
  inset-inline-end: ${rem(16)};
`

export const GridImageClickable = styled(Clickable)`
  width: 100%;
  height: 100%;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`
